import { useRef, useState } from "react";
import { ImagePlus, Loader2, X, UploadCloud } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "./adminApi";
import { imgUrl } from "../lib/spa";

const uploadFile = async (file) => {
  const fd = new FormData();
  fd.append("file", file);
  const { data } = await api.post("/admin/upload", fd, {
    headers: { "Content-Type": "multipart/form-data" },
  });
  return data.url;
};

const dropzoneBase =
  "group relative flex items-center justify-center overflow-hidden rounded-xl border border-dashed transition-colors";
const dropzoneIdle = "border-white/20 bg-black/30 hover:border-gold/60";
const dropzoneActive = "border-gold bg-gold/10";

/**
 * ImageUpload — single image with drag-and-drop from desktop.
 * Props: value, onChange, testId
 */
export const ImageUpload = ({ value, onChange, testId = "image-upload" }) => {
  const inputRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const [dragActive, setDragActive] = useState(false);

  const handleFile = async (file) => {
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      toast.error("Apenas ficheiros de imagem");
      return;
    }
    setBusy(true);
    try {
      onChange(await uploadFile(file));
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Falha no upload");
    } finally {
      setBusy(false);
    }
  };

  const onInput = async (e) => {
    await handleFile(e.target.files?.[0]);
    e.target.value = "";
  };

  const onDrop = async (e) => {
    e.preventDefault();
    setDragActive(false);
    const file = e.dataTransfer.files?.[0];
    await handleFile(file);
  };

  return (
    <div>
      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        className="hidden"
        onChange={onInput}
        data-testid={`${testId}-input`}
      />
      <button
        type="button"
        data-testid={`${testId}-button`}
        onClick={() => inputRef.current?.click()}
        onDragOver={(e) => {
          e.preventDefault();
          setDragActive(true);
        }}
        onDragLeave={() => setDragActive(false)}
        onDrop={onDrop}
        disabled={busy}
        className={`${dropzoneBase} h-40 w-32 ${dragActive ? dropzoneActive : dropzoneIdle}`}
      >
        {value ? (
          <>
            <img src={imgUrl(value)} alt="" className="h-full w-full object-cover" />
            <span className="absolute inset-0 flex items-center justify-center bg-black/60 opacity-0 transition-opacity group-hover:opacity-100">
              <ImagePlus className="h-5 w-5 text-white" strokeWidth={1.5} />
            </span>
          </>
        ) : busy ? (
          <Loader2 className="h-5 w-5 animate-spin text-gold" />
        ) : (
          <div className="flex flex-col items-center gap-1 text-white/40">
            <UploadCloud className="h-5 w-5" strokeWidth={1.5} />
            <span className="px-2 text-center text-[10px] uppercase tracking-widest">
              Arraste ou clique
            </span>
          </div>
        )}
        {busy && value && (
          <span className="absolute inset-0 flex items-center justify-center bg-black/60">
            <Loader2 className="h-5 w-5 animate-spin text-gold" />
          </span>
        )}
      </button>
    </div>
  );
};

/**
 * MultiImageUpload — gallery with drag-and-drop from desktop + reorder by drag.
 * Props: values, onChange, testId
 */
export const MultiImageUpload = ({ values = [], onChange, testId = "gallery-upload" }) => {
  const inputRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const [dragIdx, setDragIdx] = useState(null);
  const [overIdx, setOverIdx] = useState(null);
  const [dropZoneActive, setDropZoneActive] = useState(false);

  const uploadMany = async (files) => {
    if (!files.length) return;
    const images = Array.from(files).filter((f) => f.type.startsWith("image/"));
    if (images.length === 0) {
      toast.error("Apenas ficheiros de imagem");
      return;
    }
    setBusy(true);
    try {
      const urls = [];
      for (const file of images) {
        urls.push(await uploadFile(file));
      }
      onChange([...values, ...urls]);
      if (images.length > 1) toast.success(`${images.length} fotos carregadas`);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Falha no upload");
    } finally {
      setBusy(false);
    }
  };

  const handleInput = async (e) => {
    await uploadMany(e.target.files || []);
    e.target.value = "";
  };

  const handleDropZone = async (e) => {
    e.preventDefault();
    setDropZoneActive(false);
    await uploadMany(e.dataTransfer.files || []);
  };

  const moveTo = (target) => {
    if (dragIdx === null || dragIdx === target) {
      setDragIdx(null);
      setOverIdx(null);
      return;
    }
    const next = [...values];
    const [moved] = next.splice(dragIdx, 1);
    next.splice(target, 0, moved);
    onChange(next);
    setDragIdx(null);
    setOverIdx(null);
  };

  return (
    <div
      onDragOver={(e) => {
        // Only activate dropzone when dragging from OUTSIDE (files),
        // not when reordering existing thumbnails.
        if (dragIdx === null && e.dataTransfer.types.includes("Files")) {
          e.preventDefault();
          setDropZoneActive(true);
        }
      }}
      onDragLeave={(e) => {
        if (e.currentTarget === e.target) setDropZoneActive(false);
      }}
      onDrop={handleDropZone}
      className={`relative rounded-2xl border border-dashed p-3 transition-colors ${
        dropZoneActive ? "border-gold bg-gold/5" : "border-white/10 bg-black/20"
      }`}
    >
      <div className="flex flex-wrap gap-3">
        {values.map((url, i) => (
          <div
            key={`${url}-${i}`}
            draggable
            onDragStart={() => setDragIdx(i)}
            onDragOver={(e) => {
              if (dragIdx !== null) {
                e.preventDefault();
                e.stopPropagation();
                setOverIdx(i);
              }
            }}
            onDragLeave={() => setOverIdx(null)}
            onDrop={(e) => {
              if (dragIdx !== null) {
                e.stopPropagation();
                moveTo(i);
              }
            }}
            onDragEnd={() => {
              setDragIdx(null);
              setOverIdx(null);
            }}
            title="Arraste para reordenar"
            data-testid={`${testId}-item-${i}`}
            className={`relative h-28 w-24 cursor-grab overflow-hidden rounded-xl border transition-all active:cursor-grabbing ${
              overIdx === i && dragIdx !== null && dragIdx !== i
                ? "border-gold scale-105"
                : "border-white/10"
            } ${dragIdx === i ? "opacity-40" : ""}`}
          >
            <img
              src={imgUrl(url)}
              alt=""
              className="pointer-events-none h-full w-full object-cover"
            />
            <button
              type="button"
              data-testid={`${testId}-remove-${i}`}
              onClick={() => onChange(values.filter((_, j) => j !== i))}
              className="absolute right-1 top-1 flex h-6 w-6 items-center justify-center rounded-full bg-black/70 text-white transition-colors hover:bg-red-500"
            >
              <X className="h-3.5 w-3.5" />
            </button>
          </div>
        ))}
        <input
          ref={inputRef}
          type="file"
          accept="image/*"
          multiple
          className="hidden"
          onChange={handleInput}
          data-testid={`${testId}-input`}
        />
        <button
          type="button"
          data-testid={`${testId}-button`}
          onClick={() => inputRef.current?.click()}
          disabled={busy}
          className="flex h-28 w-24 flex-col items-center justify-center gap-1 rounded-xl border border-dashed border-white/20 bg-black/30 transition-colors hover:border-gold/60"
        >
          {busy ? (
            <Loader2 className="h-5 w-5 animate-spin text-gold" />
          ) : (
            <>
              <UploadCloud className="h-5 w-5 text-white/40" strokeWidth={1.5} />
              <span className="px-2 text-center text-[10px] uppercase tracking-widest text-white/40">
                Arraste ou clique
              </span>
            </>
          )}
        </button>
      </div>
      {dropZoneActive && (
        <div className="pointer-events-none absolute inset-0 flex items-center justify-center rounded-2xl bg-gold/10 backdrop-blur-sm">
          <p className="text-sm font-medium uppercase tracking-widest text-gold">
            Solte as fotos aqui
          </p>
        </div>
      )}
    </div>
  );
};
